import React, { useState, useEffect } from 'react';
import { Button } from '@appkit4/react-components';
import Alert from '../../assets/images/Alert.svg';
import './EmptySessionError.scss';

interface EmptySessionErrorProps {
    sessionId: string;
    onRetryWithPrompt?: (prompt: string) => void;
    onDismiss?: () => void;
}

const EmptySessionError: React.FC<EmptySessionErrorProps> = ({ sessionId, onRetryWithPrompt, onDismiss }) => {
    const [storedPrompt, setStoredPrompt] = useState<string | null>(null);

    useEffect(() => {
        const storedPromptKey = `rapidcraft-prompt-${sessionId}`;
        const prompt = localStorage.getItem(storedPromptKey);
        setStoredPrompt(prompt);
    }, [sessionId]);
    return (
        <div className="empty-session-error">
            <div className="empty-session-content">
                <div className="error-icon">
                    <Alert />
                </div>
                <h2 className="error-title">{storedPrompt ? 'Generation Failed' : 'Session Data Not Found'}</h2>
                <p className="error-message">
                    {storedPrompt
                        ? 'Something went wrong during code generation. The generation may have failed or been interrupted.'
                        : "This session doesn't have any generated code yet or the data couldn't be loaded."}
                </p>

                {storedPrompt && (
                    <div className="stored-prompt-info">
                        <p className="prompt-label">Original prompt:</p>
                        <div className="prompt-preview">
                            "{storedPrompt.length > 150 ? `${storedPrompt.substring(0, 150)}...` : storedPrompt}"
                        </div>
                    </div>
                )}

                <div className="error-suggestions">
                    <p>This might happen if:</p>
                    <ul>
                        {storedPrompt ? (
                            <>
                                <li>The AI generation failed or timed out</li>
                                <li>There was a network error during generation</li>
                                <li>The session was interrupted</li>
                            </>
                        ) : (
                            <>
                                <li>The session is new and no code has been generated</li>
                                <li>The session data is still loading</li>
                                <li>The session was deleted or doesn't exist</li>
                                <li>There was a network error fetching the data</li>
                            </>
                        )}
                    </ul>
                </div>

                <div className="error-actions">
                    {storedPrompt && onRetryWithPrompt ? (
                        <>
                            <Button
                                kind="primary"
                                onClick={() => onRetryWithPrompt(storedPrompt)}
                                aria-label="Retry generation with original prompt">
                                Retry Generation
                            </Button>
                            {onDismiss && (
                                <Button
                                    kind="secondary"
                                    onClick={onDismiss}
                                    aria-label="Dismiss and keep empty session">
                                    Dismiss
                                </Button>
                            )}
                        </>
                    ) : onDismiss ? (
                        <Button kind="secondary" onClick={onDismiss} aria-label="Close error message">
                            Close
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default EmptySessionError;
